<template>
  <q-skeleton type="rect" height="200px" class="q-mt-sm" v-if="loading || albumViewsLoading" />
  <div class="album-views__table" v-if="!loading && !albumViewsLoading">
    <div class="album-views__table-header">
      <h2 class="album-views__table-title">
        {{ $t("dashboard.albumViewsTable.title") }}
      </h2>
      <q-input
        autofocus
        v-model="bibNumber"
        @update:model-value="val => filterEventsByNumber(val)"
        dense
        :label="$t('dashboard.albumViewsTable.filterAlbums')"
        class="album-views__table-input"
      >
      </q-input>
    </div>
    <div class="album-views__table-wrapper">
      <div v-if="filteredAlbumViews.length === 0" class="album-views__table-empty">
        {{ $t("dashboard.albumViewsTable.noData") }}
      </div>
      <q-table
        v-else
        v-model:pagination="pagination"
        row-key="bibNumber"
        :rows="filteredAlbumViews"
        :columns="tableColumns"
        :rows-per-page-options="[5, 10, 15]"
        :loading="albumViewsLoading"
        @request="onRequest"
      >
        <template v-slot:body="props">
          <q-tr :props="props">
            <q-td v-for="col in tableColumns" :key="col.name" :props="props">
              {{ col.field === 'bib_number' ? '#' : '' }}{{ props.row[col.field] }}
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';

export default {
  name: "AlbumViewsTable",
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      bibNumber: null,
      filteredAlbumViews: [],
      pagination: {
        page: 1,
        rowsPerPage: 10,
        rowsNumber: 0,
        sortBy: "bibNumber",
        descending: false,
      },
    };
  },
  computed: {
    ...mapState("dashboardEvents", [
      "loading",
      "albumViewsData",
      "albumViewsLoading",
      "albumsTableItemCount",
    ]),
    tableColumns() {
      return [
        {
          name: "bibNumber",
          label: this.$t("dashboard.albumViewsTable.albumNumber"),
          align: "left",
          field: "bib_number",
          sortable: true,
        },
        {
          name: "viewCount",
          label: this.$t("dashboard.albumViewsTable.views"),
          align: "center",
          field: "view_count",
          sortable: true,
        }
      ];
    },
  },
  methods: {
    ...mapActions("dashboardEvents", ["fetchEventAlbumViewsData"]),
    filterEventsByNumber: debounce(function (bibNumber) {
      return bibNumber
        ? this.fetchData(bibNumber)
        : this.fetchData();
    }, 300),
    async fetchData(bibNumber = 0) {
      const { page, rowsPerPage } = this.pagination;

      await this.fetchEventAlbumViewsData({
        eventId: this.$route.params.eventId,
        page,
        perPage: rowsPerPage,
        bibNumber: bibNumber
      });

      this.pagination.rowsNumber = this.albumsTableItemCount;
      this.filteredAlbumViews = this.albumViewsData.data;
    },
    async onRequest(props) {
      this.pagination = {
        ...props.pagination,
        rowsNumber: this.albumsTableItemCount,
      };

      await this.fetchData();
    },
  },
  async created() {
    await this.fetchData()
  }
};
</script>

<style lang="scss" scoped>
.album-views__table {
  margin-top: 10px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  -webkit-box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  -moz-box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &-input{
    min-width: 280px;
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #e9ecef;
  }

  &-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: $secondary;
    margin: 0;
  }

  &-link {
    text-decoration: none;
  }

  &-wrapper {
    padding-bottom: 8px;
    .q-table__card {
      box-shadow: none;
    }
  }

  &-empty {
    text-align: center;
    padding: 20px;
    color: #6c757d;
    font-size: 1rem;
  }
}

.q-table {
  th {
    font-weight: 600;
    background-color: #f1f3f5;
    color: #495057;
    font-size: 0.8rem;
    padding: 12px 12px;
    border-bottom: 2px solid #dee2e6;

    &.text-left {
      text-align: left;
    }

    &.text-center {
      text-align: center;
    }

    &.text-right {
      text-align: right;
    }

    &:hover {
      background-color: #e9ecef;
    }
  }

  tbody td {
    padding: 12px 8px;
    font-size: 0.7rem;
    color: #212529;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
}

.q-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

@media (max-width: $breakpoint-xs-max) {
  .album-views__table-header {
    flex-direction: column;
  }

  .album-views__table-title {
    font-size: 1.3rem;
  }

  .summary-label {
    font-size: 0.9rem;
  }
}
</style>
